import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  collection, query, where, onSnapshot, getDocs, getDoc, doc, setDoc, deleteDoc, writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { db, auth } from '../firebase';
import Papa from 'papaparse';
import { useModuleAdmin } from '../useModuleAdmin';
import { ACTOR_BUCKET_FOR, normaliseRole, isAdminRole } from '../roles';
import HeaderActionsPortal from './HeaderActionsPortal';
import {
  Accessibility, ClipboardList, Settings as SettingsIcon, Plus, X, Pencil, Trash2,
  Search, AlertTriangle, CheckCircle2, DoorOpen, Clock, Upload, Download, ListChecks, GripVertical,
  ChevronUp, ChevronDown, ArrowDownWideNarrow,
} from 'lucide-react';
import {
  DEFAULT_ACCOMMODATIONS, DEFAULT_EXTRA_MINUTES, roomTiming, roomExtraMinutes,
  resolveChecklistTime,
  senStudentsForSession, unplacedStudents, placedEmails, bySessionOrder, byClassThenNumber,
  parseRegisterCsv, registerCsvTemplate,
  CHECKLIST_ANCHOR, DEFAULT_CHECKLIST, newChecklistItem, resolveChecklist, cleanChecklist,
  moveChecklistItem, sortChecklistByTime, checklistIsOutOfOrder,
} from '../senInvigilation';
import { useT } from '../i18n.jsx';
import '../theme-palette.css';

/**
 * SEN Invigilation -- the special rooms that run alongside an exam sitting.
 *
 * An extension of Invigilation, not a second timetable. Every special room points at a real
 * invigilation_session and takes its date, subject, paper and start time from there; what it owns
 * is the venue, the invigilators, the end time (extra time runs past the main hall) and who is in
 * it. Move the exam in Invigilation and the room follows, because it never kept its own copy.
 *
 * Two collections because they are two different secrets:
 *
 *   sen_student       the register -- which student holds which accommodation. Confidential.
 *                     Readable only by this module's admins.
 *   sen_invigilation  a room. A duty roster like any other, staff-readable, and it carries the
 *                     names of the students in it because the invigilator standing in that room
 *                     has to know who belongs there -- and cannot read the register.
 *
 * That split is the reason a room stores its students rather than pointing at them.
 */

const actorRoleBucket = (rawRole) => ACTOR_BUCKET_FOR[normaliseRole(rawRole)] || 'Denied';

const card = (isMobile) => ({
  backgroundColor: 'var(--color-bg-card)',
  borderRadius: isMobile ? 0 : 'var(--radius-lg)',
  boxShadow: isMobile ? 'none' : 'var(--shadow-sm)',
  overflow: 'hidden',
});

const th = { padding: '0.5rem 0.7rem', fontWeight: '600', fontSize: '0.75rem', color: 'var(--color-text-subtle)', textAlign: 'left', whiteSpace: 'nowrap' };
const td = { padding: '0.5rem 0.7rem', borderTop: '1px solid var(--color-border)', fontSize: '0.875rem', verticalAlign: 'top' };
const rowBtn = { background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: '0.2rem' };
const chip = {
  display: 'inline-flex', alignItems: 'center', gap: '0.2rem', padding: '0.1rem 0.5rem',
  borderRadius: '999px', fontSize: '0.72rem', fontWeight: '600',
  backgroundColor: 'var(--color-bg-subtle)', color: 'var(--color-text-subtle)',
};
const selectStyle = { padding: '0.45rem 0.6rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', fontSize: '0.85rem', backgroundColor: 'var(--color-bg-card)', color: 'var(--color-text-main)' };
const modalBtn = { width: 'auto', padding: '0.55rem 1.1rem' };

const emptyRoom = () => ({
  id: null, sessionId: '', venue: '', extraMinutes: DEFAULT_EXTRA_MINUTES,
  invigilators: [], students: [], checklist: [],
});

const emptyEntry = () => ({
  email: '', sid: '', name: '', currentClass: '', classNumber: '',
  accommodations: [], extraMinutes: DEFAULT_EXTRA_MINUTES, notes: '', active: true,
});


/**
 * The running order: what the invigilator does, and when.
 *
 * `times` is the room this list belongs to, so each row can show the clock time it lands on. In
 * Settings there is no room, so rows describe themselves instead ("15 min before the end") -- which
 * is the honest thing to show for a template that will be applied to papers of every length.
 */
const ChecklistEditor = ({ items, times, onChange, isMobile }) => {
  const { t } = useT();
  // Held here rather than in the parent: which row is in the air is a fact about this widget while
  // a pointer is down, and nothing outside it has any use for it.
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const setItem = (idx, patch) => onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const removeItem = (idx) => onChange(items.filter((_, i) => i !== idx));
  const addItem = () => onChange([...items, newChecklistItem(items.length + 1)]);
  const move = (from, to) => onChange(moveChecklistItem(items, from, to));

  const endDrag = () => { setDragIdx(null); setOverIdx(null); };
  const handleDrop = (to) => {
    if (dragIdx !== null) move(dragIdx, to);
    endDrag();
  };

  const outOfOrder = times ? checklistIsOutOfOrder(items, times) : false;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
      {items.length === 0 && (
        <span style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>
          Nothing to do yet. Add the first step.
        </span>
      )}

      {items.map((item, idx) => {
        const resolved = times ? resolveChecklistTime(item, times) : '';
        const isDragging = dragIdx === idx;
        const isTarget = overIdx === idx && dragIdx !== null && dragIdx !== idx;
        return (
          <div key={item.id || idx}
            onDragOver={(e) => { e.preventDefault(); if (overIdx !== idx) setOverIdx(idx); }}
            onDrop={(e) => { e.preventDefault(); handleDrop(idx); }}
            style={{
              display: 'flex', gap: '0.35rem', alignItems: 'center', flexWrap: isMobile ? 'wrap' : 'nowrap',
              opacity: isDragging ? 0.4 : 1,
              // A line where the row will land, rather than moving everything about under the
              // pointer -- the row being dragged is the only thing that should appear to move.
              boxShadow: isTarget
                ? (dragIdx > idx ? 'inset 0 2px 0 var(--color-accent)' : 'inset 0 -2px 0 var(--color-accent)')
                : 'none',
              borderRadius: 'var(--radius-md)',
              transition: 'opacity 0.12s ease',
            }}
          >
            {/* Only the handle is draggable, so selecting text in the instruction field does not
                pick the whole row up instead. */}
            <span
              draggable
              onDragStart={(e) => { setDragIdx(idx); e.dataTransfer.effectAllowed = 'move'; }}
              onDragEnd={endDrag}
              aria-label={t('senInvigilation.dragToReorder')}
              style={{ display: 'flex', flexShrink: 0, cursor: 'grab', color: 'var(--color-border-strong)', touchAction: 'none' }}
            >
              <GripVertical size={14} />
            </span>

            {/* Dragging is a mouse gesture and this runs as an installed app on phones and tablets,
                where HTML5 drag events never fire at all. These are not a fallback nobody uses --
                on a tablet they are the only way to reorder anything. */}
            <div style={{ display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
              <button type="button" onClick={() => move(idx, idx - 1)} disabled={idx === 0}
                aria-label={t('senInvigilation.moveUp')}
                style={{ ...rowBtn, padding: 0, color: idx === 0 ? 'var(--color-border)' : 'var(--color-text-muted)', cursor: idx === 0 ? 'default' : 'pointer' }}>
                <ChevronUp size={13} />
              </button>
              <button type="button" onClick={() => move(idx, idx + 1)} disabled={idx === items.length - 1}
                aria-label={t('senInvigilation.moveDown')}
                style={{ ...rowBtn, padding: 0, color: idx === items.length - 1 ? 'var(--color-border)' : 'var(--color-text-muted)', cursor: idx === items.length - 1 ? 'default' : 'pointer' }}>
                <ChevronDown size={13} />
              </button>
            </div>

            <select
              value={item.when} onChange={e => setItem(idx, { when: e.target.value })}
              style={{ ...selectStyle, flex: '0 0 auto', width: isMobile ? '48%' : '9.5rem' }}
            >
              <option value={CHECKLIST_ANCHOR.START}>{t('senInvigilation.afterStart')}</option>
              <option value={CHECKLIST_ANCHOR.END}>{t('senInvigilation.beforeEnd')}</option>
              <option value={CHECKLIST_ANCHOR.CLOCK}>{t('senInvigilation.atTheTime')}</option>
            </select>

            {item.when === CHECKLIST_ANCHOR.CLOCK ? (
              <input
                type="time" className="input-field" value={item.at || ''}
                onChange={e => setItem(idx, { at: e.target.value })}
                style={{ flex: '0 0 auto', width: isMobile ? '48%' : '7rem', padding: '0.4rem 0.5rem', fontSize: '0.85rem' }}
              />
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', flex: '0 0 auto', width: isMobile ? '48%' : '7rem' }}>
                <input
                  type="number" min="0" step="1" className="input-field" value={item.offset ?? 0}
                  onChange={e => setItem(idx, { offset: e.target.value })}
                  style={{ padding: '0.4rem 0.5rem', fontSize: '0.85rem' }}
                />
                <span style={{ fontSize: '0.76rem', color: 'var(--color-text-muted)' }}>{t('senInvigilation.min')}</span>
              </div>
            )}

            <input
              className="input-field" value={item.text}
              onChange={e => setItem(idx, { text: e.target.value })}
              placeholder={t('senInvigilation.stepPlaceholder')}
              style={{ flex: '1 1 12rem', minWidth: 0, padding: '0.4rem 0.6rem', fontSize: '0.85rem' }}
            />

            {/* The whole point of anchoring: this number moves on its own when the room's extra
                time changes, instead of the school having to remember to retype it. */}
            <span style={{ flex: '0 0 auto', width: '3.4rem', textAlign: 'right', fontSize: '0.78rem', fontWeight: '700', color: resolved ? 'var(--color-primary)' : 'var(--color-text-muted)' }}>
              {resolved || (times ? '--:--' : '')}
            </span>

            <button type="button" onClick={() => removeItem(idx)} aria-label={t('senInvigilation.removeStep')}
              style={{ ...rowBtn, color: 'var(--color-danger)', flexShrink: 0 }}>
              <Trash2 size={15} />
            </button>
          </div>
        );
      })}

      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <button type="button" className="btn-outline" onClick={addItem}
          style={{ width: 'auto', padding: '0.4rem 0.9rem', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
          <Plus size={14} /> {t('senInvigilation.addStep')}
        </button>

        {/* Offered, never done automatically. Two steps often land on the same minute -- collect
            the scripts, then dismiss the room -- and the clock has nothing to order those by, so
            re-sorting on every change would shuffle them behind the school's back. */}
        {outOfOrder && (
          <button type="button" className="btn-outline" onClick={() => onChange(sortChecklistByTime(items, times))}
            style={{ width: 'auto', padding: '0.4rem 0.9rem', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.3rem', borderColor: 'var(--color-warning)', color: 'var(--color-warning-text)' }}>
            <ArrowDownWideNarrow size={14} /> {t('senInvigilation.notInClockOrder')}
          </button>
        )}
      </div>
    </div>
  );
};

const SENInvigilation = () => {
  const { t } = useT();
  // The same three cases describeChecklistItem covers, in the reader's language.
  const describeItem = (item) => {
    const offset = Number(item?.offset) || 0;
    if (item?.when === CHECKLIST_ANCHOR.CLOCK) return item.at || t('senInvigilation.describe.noTimeSet');
    if (item?.when === CHECKLIST_ANCHOR.END) {
      return offset === 0
        ? t('senInvigilation.describe.atTheEnd')
        : t('senInvigilation.describe.beforeEnd', { count: offset });
    }
    return offset === 0
      ? t('senInvigilation.describe.asItStarts')
      : t('senInvigilation.describe.afterStart', { count: offset });
  };
  const { activeSchoolId, userProfile, isMobile, closeSidebar } = useOutletContext();
  const [activeTab, setActiveTab] = useState('rooms');

  const ownRole = String(userProfile?.role || '').toLowerCase();
  const isAdmin = isAdminRole(ownRole);
  const ownEmail = (auth.currentUser?.email || '').toLowerCase();
  const ownActorRole = actorRoleBucket(ownRole);
  const isModuleAdmin = useModuleAdmin(activeSchoolId, 'sen_invigilation');

  // --- Reference data -------------------------------------------------------------------------
  const [academicYears, setAcademicYears] = useState([]);
  const [schoolYear, setSchoolYear] = useState('');
  const [assessments, setAssessments] = useState([]);
  const [assessment, setAssessment] = useState('');
  const [locations, setLocations] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [studentList, setStudentList] = useState([]);

  useEffect(() => {
    if (!activeSchoolId) return;
    (async () => {
      try {
        const ySnap = await getDocs(collection(db, 'schools', activeSchoolId, 'academic_years'));
        const yList = [];
        ySnap.forEach(d => yList.push({ uid: d.id, ...d.data() }));
        yList.sort((a, b) => (a.yearId || '').localeCompare(b.yearId || ''));
        setAcademicYears(yList);
        if (yList.length > 0) {
          const cur = yList.find(y => y.cur_year) || yList[yList.length - 1];
          setSchoolYear(cur.yearId);
        }
      } catch (e) { console.error('Error loading academic years', e); }

      try {
        const sysSnap = await getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'system_settings'));
        const dropdowns = sysSnap.exists() ? (sysSnap.data().dropdowns || {}) : {};
        // Same filter as Invigilation: only assessments actually sat as a written paper have rooms.
        const assessList = (dropdowns.assessments || []).filter(a => a.active !== false && a.writtenTest !== false);
        setAssessments(assessList);
        if (assessList.length > 0) setAssessment(assessList[0].code);
        setLocations((dropdowns.locations || []).filter(l => l.active !== false));
      } catch (e) { console.error('Error loading system settings', e); }

      try {
        const staffSnap = await getDocs(collection(db, 'schools', activeSchoolId, 'staff_profiles'));
        const list = [];
        staffSnap.forEach(d => {
          const data = d.data();
          if (!data.initial) return;
          list.push({
            email: d.id,
            initial: data.initial,
            name: `${data.lastName || ''} ${data.firstName || ''}`.trim() || data.initial,
          });
        });
        list.sort((a, b) => a.initial.localeCompare(b.initial));
        setStaffList(list);
      } catch (e) { console.error('Error loading staff list', e); }
    })();
  }, [activeSchoolId]);

  // The roll is only needed to ADD somebody to the register, so it is loaded for the people who can
  // do that. Everyone else works entirely from the rooms, which carry their own names.
  useEffect(() => {
    if (!activeSchoolId || !(isAdmin || isModuleAdmin)) { setStudentList([]); return; }
    getDocs(collection(db, 'schools', activeSchoolId, 'student_profiles'))
      .then(snap => {
        const list = [];
        snap.forEach(d => {
          const data = d.data();
          if (!data.sid) return;
          list.push({
            email: d.id,
            sid: data.sid,
            name: `${data.lastName || ''} ${data.firstName || ''}`.trim() || data.name || 'Unknown',
            currentClass: data.currentClass || '',
            classNumber: data.classNumber || '',
          });
        });
        list.sort(byClassThenNumber);
        setStudentList(list);
      })
      .catch(e => console.error('Error loading student list', e));
  }, [activeSchoolId, isAdmin, isModuleAdmin]);

  // --- Settings -------------------------------------------------------------------------------
  const [editPermissions, setEditPermissions] = useState({ roles: [] });
  const [accommodations, setAccommodations] = useState(DEFAULT_ACCOMMODATIONS);
  const [checklistTemplate, setChecklistTemplate] = useState(DEFAULT_CHECKLIST);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    if (!activeSchoolId) return;
    getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'sen_invigilation_settings'))
      .then(snap => {
        const data = snap.exists() ? snap.data() : {};
        setEditPermissions({ roles: data.editPermissions?.roles || [] });
        // A school that has never opened Settings gets the house list rather than an empty picker.
        setAccommodations(data.accommodations?.length ? data.accommodations : DEFAULT_ACCOMMODATIONS);
        // A school that has deliberately emptied the template keeps it empty -- only a document
        // that has never had the field gets the house default.
        setChecklistTemplate(data.checklistTemplate ?? DEFAULT_CHECKLIST);
      })
      .catch(e => console.error('Error loading SEN invigilation settings', e));
  }, [activeSchoolId]);

  // Admins are always in, so a misconfigured matrix cannot lock out the people who would fix it.
  const canManage = isAdmin || isModuleAdmin || editPermissions.roles.includes(ownActorRole);
  // The register is the confidential half; the rules only open it to this module's admins, so the
  // screen must not offer it to anyone else either.
  const canSeeRegister = isAdmin || isModuleAdmin;

  const accommodationLabel = useMemo(
    () => Object.fromEntries(accommodations.map(a => [a.code, a.label])),
    [accommodations],
  );

  // --- Parent sessions (read-only here; Invigilation owns them) -------------------------------
  const [sessions, setSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);

  useEffect(() => {
    if (!activeSchoolId || !schoolYear || !assessment) { setSessions([]); return undefined; }
    setLoadingSessions(true);
    const q = query(
      collection(db, 'schools', activeSchoolId, 'invigilation_session'),
      where('schoolYear', '==', schoolYear),
      where('assessment', '==', assessment),
    );
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      setSessions(list.filter(s => s.date).sort(bySessionOrder));
      setLoadingSessions(false);
    }, (e) => { console.error('Error loading invigilation sessions', e); setLoadingSessions(false); });
    return () => unsub();
  }, [activeSchoolId, schoolYear, assessment]);

  // --- Rooms ----------------------------------------------------------------------------------
  const [rooms, setRooms] = useState([]);

  useEffect(() => {
    if (!activeSchoolId || !schoolYear || !assessment) { setRooms([]); return undefined; }
    const q = query(
      collection(db, 'schools', activeSchoolId, 'sen_invigilation'),
      where('schoolYear', '==', schoolYear),
      where('assessment', '==', assessment),
    );
    const unsub = onSnapshot(q, (snap) => {
      const list = [];
      snap.forEach(d => list.push({ id: d.id, ...d.data() }));
      setRooms(list);
    }, (e) => console.error('Error loading SEN rooms', e));
    return () => unsub();
  }, [activeSchoolId, schoolYear, assessment]);

  const roomsBySession = useMemo(() => {
    const map = {};
    rooms.forEach(r => {
      if (!map[r.sessionId]) map[r.sessionId] = [];
      map[r.sessionId].push(r);
    });
    Object.values(map).forEach(list => list.sort((a, b) => String(a.venue || '').localeCompare(String(b.venue || ''))));
    return map;
  }, [rooms]);

  // --- Register -------------------------------------------------------------------------------
  const [register, setRegister] = useState([]);

  useEffect(() => {
    if (!activeSchoolId || !canSeeRegister) { setRegister([]); return undefined; }
    const unsub = onSnapshot(
      collection(db, 'schools', activeSchoolId, 'sen_student'),
      (snap) => {
        const list = [];
        snap.forEach(d => list.push({ email: d.id, ...d.data() }));
        setRegister(list.sort(byClassThenNumber));
      },
      (e) => console.error('Error loading SEN register', e),
    );
    return () => unsub();
  }, [activeSchoolId, canSeeRegister]);

  // Sessions worth showing: the ones with a registered SEN student in that class, plus any that
  // already have a room (so a room never becomes invisible because a student left the class).
  const relevantSessions = useMemo(() => sessions.filter(s => (
    senStudentsForSession(s, register).length > 0 || (roomsBySession[s.id] || []).length > 0
  )), [sessions, register, roomsBySession]);

  const sessionsByDate = useMemo(() => {
    const map = {};
    relevantSessions.forEach(s => {
      if (!map[s.date]) map[s.date] = [];
      map[s.date].push(s);
    });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  }, [relevantSessions]);

  // --- Room editor ----------------------------------------------------------------------------
  const [roomForm, setRoomForm] = useState(null); // emptyRoom() shape, or null when closed
  const [savingRoom, setSavingRoom] = useState(false);

  const roomSession = useMemo(
    () => sessions.find(s => s.id === roomForm?.sessionId) || null,
    [sessions, roomForm],
  );

  // A new room starts from the school's own template, because the routine is the same every paper
  // and retyping it is how it ends up different between rooms by accident.
  const openNewRoom = (session) => {
    if (closeSidebar) closeSidebar();
    setRoomForm({
      ...emptyRoom(),
      sessionId: session.id,
      checklist: checklistTemplate.map((it, i) => ({ ...it, id: `n${i}` })),
    });
  };

  const openEditRoom = (room) => {
    if (closeSidebar) closeSidebar();
    setRoomForm({
      id: room.id,
      sessionId: room.sessionId,
      venue: room.venue || '',
      extraMinutes: room.extraMinutes ?? DEFAULT_EXTRA_MINUTES,
      invigilators: room.invigilators || [],
      students: room.students || [],
      // Rooms saved before the running order existed carried a free-text note. Rather than losing
      // it, it becomes the first step -- unresolved as to timing, which is exactly what it was.
      checklist: (room.checklist && room.checklist.length)
        ? room.checklist
        : (room.notes ? [{ ...newChecklistItem(0), text: room.notes }] : []),
    });
  };

  // Candidates for this room: everybody registered for the session who is not already sitting in a
  // DIFFERENT room, plus whoever is already in this one.
  const roomCandidates = useMemo(() => {
    if (!roomSession) return [];
    const otherRooms = (roomsBySession[roomSession.id] || []).filter(r => r.id !== roomForm?.id);
    const taken = placedEmails(otherRooms);
    return senStudentsForSession(roomSession, register).filter(s => !taken.has(String(s.email).toLowerCase()));
  }, [roomSession, roomsBySession, roomForm, register]);

  const toggleRoomStudent = (student) => {
    setRoomForm(prev => {
      const has = prev.students.some(s => s.email === student.email);
      const students = has
        ? prev.students.filter(s => s.email !== student.email)
        : [...prev.students, {
          email: student.email, sid: student.sid || '', name: student.name || '',
          currentClass: student.currentClass || '', classNumber: student.classNumber || '',
          accommodations: student.accommodations || [],
          extraMinutes: student.extraMinutes || 0,
        }];
      return { ...prev, students };
    });
  };

  // Suggest the longest grant in the room, but leave it editable: a school may run one room at a
  // flat allowance regardless of who is in it.
  const suggestedMinutes = useMemo(() => {
    const picked = roomCandidates.filter(c => roomForm?.students?.some(s => s.email === c.email));
    return roomExtraMinutes(picked);
  }, [roomCandidates, roomForm]);

  const roomTimes = useMemo(() => roomTiming({
    startTime: roomSession?.startTime,
    duration: roomSession?.duration,
    extraMinutes: roomForm?.extraMinutes,
  }), [roomSession, roomForm]);

  const handleSaveRoom = async (e) => {
    e.preventDefault();
    if (!roomSession || savingRoom) return;
    setSavingRoom(true);
    try {
      const id = roomForm.id || doc(collection(db, 'schools', activeSchoolId, 'sen_invigilation')).id;
      await setDoc(doc(db, 'schools', activeSchoolId, 'sen_invigilation', id), {
        schoolYear,
        assessment,
        sessionId: roomSession.id,
        // Denormalised from the parent so a room reads on its own -- an invigilator opening their
        // duty should not need a second lookup to learn which paper they are watching. The parent
        // stays the source of truth: these are refreshed on every save.
        class: roomSession.class || '',
        subject: roomSession.subject || '',
        paper: roomSession.paper || '',
        date: roomSession.date || '',
        startTime: roomSession.startTime || '',
        baseDuration: Number(roomSession.duration) || 0,
        venue: roomForm.venue || '',
        extraMinutes: roomTimes.extraMinutes,
        duration: roomTimes.duration,
        endTime: roomTimes.endTime,
        invigilators: roomForm.invigilators,
        students: roomForm.students,
        checklist: cleanChecklist(roomForm.checklist),
        // Cleared rather than left behind: an old note still sitting on the document would be a
        // second set of instructions that nothing displays.
        notes: '',
        updatedAt: serverTimestamp(),
        updatedByEmail: ownEmail,
      }, { merge: true });
      setRoomForm(null);
    } catch (err) {
      console.error('Error saving SEN room', err);
      alert(t('senInvigilation.saveRoomFailed', { message: err.message }));
    } finally {
      setSavingRoom(false);
    }
  };

  const handleDeleteRoom = async (room) => {
    const n = (room.students || []).length;
    const where = room.venue ? t('senInvigilation.deleteRoomWhere', { venue: room.venue }) : '';
    if (!window.confirm(t('senInvigilation.deleteRoomConfirm', { where, count: n }))) return;
    try {
      await deleteDoc(doc(db, 'schools', activeSchoolId, 'sen_invigilation', room.id));
    } catch (err) {
      console.error('Error deleting SEN room', err);
      alert(t('senInvigilation.deleteRoomFailed', { message: err.message }));
    }
  };

  // --- Register editor ------------------------------------------------------------------------
  const [entryForm, setEntryForm] = useState(null);
  const [savingEntry, setSavingEntry] = useState(false);
  const [registerSearch, setRegisterSearch] = useState('');
  const [studentSearch, setStudentSearch] = useState('');

  const filteredRegister = useMemo(() => {
    const term = registerSearch.trim().toLowerCase();
    if (!term) return register;
    return register.filter(s => (
      String(s.name || '').toLowerCase().includes(term)
      || String(s.sid || '').toLowerCase().includes(term)
      || String(s.currentClass || '').toLowerCase().includes(term)
    ));
  }, [register, registerSearch]);

  const studentPickList = useMemo(() => {
    const term = studentSearch.trim().toLowerCase();
    const already = new Set(register.map(r => String(r.email).toLowerCase()));
    const pool = studentList.filter(s => !already.has(String(s.email).toLowerCase()));
    if (!term) return pool.slice(0, 40);
    return pool.filter(s => (
      String(s.name || '').toLowerCase().includes(term)
      || String(s.sid || '').toLowerCase().includes(term)
      || String(s.currentClass || '').toLowerCase().includes(term)
    )).slice(0, 40);
  }, [studentList, register, studentSearch]);

  const openNewEntry = () => {
    if (closeSidebar) closeSidebar();
    setStudentSearch('');
    setEntryForm(emptyEntry());
  };

  const openEditEntry = (entry) => {
    if (closeSidebar) closeSidebar();
    setEntryForm({
      email: entry.email,
      sid: entry.sid || '',
      name: entry.name || '',
      currentClass: entry.currentClass || '',
      classNumber: entry.classNumber || '',
      accommodations: entry.accommodations || [],
      extraMinutes: entry.extraMinutes ?? DEFAULT_EXTRA_MINUTES,
      notes: entry.notes || '',
      active: entry.active !== false,
    });
  };

  const toggleEntryAccommodation = (code) => {
    setEntryForm(prev => ({
      ...prev,
      accommodations: prev.accommodations.includes(code)
        ? prev.accommodations.filter(c => c !== code)
        : [...prev.accommodations, code],
    }));
  };

  const handleSaveEntry = async (e) => {
    e.preventDefault();
    if (!entryForm?.email || savingEntry) return;
    setSavingEntry(true);
    try {
      // Keyed by address, like every other record on the platform. The SID and class are display
      // only and are refreshed from the roll whenever the entry is edited.
      await setDoc(doc(db, 'schools', activeSchoolId, 'sen_student', entryForm.email), {
        sid: entryForm.sid,
        name: entryForm.name,
        currentClass: entryForm.currentClass,
        classNumber: entryForm.classNumber,
        accommodations: entryForm.accommodations,
        extraMinutes: Number(entryForm.extraMinutes) || 0,
        notes: entryForm.notes || '',
        active: entryForm.active !== false,
        updatedAt: serverTimestamp(),
        updatedByEmail: ownEmail,
      }, { merge: true });
      setEntryForm(null);
    } catch (err) {
      console.error('Error saving SEN register entry', err);
      alert(t('senInvigilation.saveEntryFailed', { message: err.message }));
    } finally {
      setSavingEntry(false);
    }
  };


  // --- Importing the school's own spreadsheet --------------------------------------------------
  //
  // Nothing is written until the preview has been read and confirmed. A register import is exactly
  // the kind of job where a mistyped student number silently drops a student -- and a dropped
  // student sits their exam in the main hall with no accommodation and nobody notices until it is
  // over. So the file is parsed, matched against the roll, and shown back before anything moves.
  const fileInputRef = useRef(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);

  const handleChooseFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // so choosing the same file twice still fires
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const { entries, problems, columns } = parseRegisterCsv(results.data, {
          students: studentList,
          accommodations,
          t,
        });
        const known = new Set(entries.map(en => en.email));
        setImportPreview({
          fileName: file.name,
          entries,
          problems,
          columns,
          // Merged, not replaced -- so say what stays behind rather than letting the school assume
          // the file is now the whole truth.
          untouched: register.filter(r => !known.has(r.email)),
          updating: entries.filter(en => register.some(r => r.email === en.email)).length,
        });
      },
      error: (err) => {
        console.error('Could not read that file', err);
        alert(t('senInvigilation.readFileFailed', { message: err.message }));
      },
    });
  };

  const handleConfirmImport = async () => {
    if (!importPreview || importing) return;
    setImporting(true);
    try {
      // Chunked well under Firestore's 500-op limit, so a whole-school register still lands.
      const CHUNK = 400;
      for (let i = 0; i < importPreview.entries.length; i += CHUNK) {
        const batch = writeBatch(db);
        importPreview.entries.slice(i, i + CHUNK).forEach(en => {
          batch.set(doc(db, 'schools', activeSchoolId, 'sen_student', en.email), {
            sid: en.sid,
            name: en.name,
            currentClass: en.currentClass,
            classNumber: en.classNumber,
            accommodations: en.accommodations,
            extraMinutes: en.extraMinutes,
            notes: en.notes,
            active: en.active,
            updatedAt: serverTimestamp(),
            updatedByEmail: ownEmail,
          }, { merge: true });
        });
        await batch.commit();
      }
      setImportPreview(null);
    } catch (err) {
      console.error('Error importing SEN register', err);
      alert(t('senInvigilation.importFailed', { message: err.message }));
    } finally {
      setImporting(false);
    }
  };

  // A template with this school's own accommodation names as the column headings, so the file the
  // school fills in is the file the import already understands.
  const handleDownloadTemplate = () => {
    const headers = registerCsvTemplate(accommodations);
    const example = Object.fromEntries(headers.map(h => [h, '']));
    if (studentList.length) {
      example.SID = studentList[0].sid;
      example.Name = studentList[0].name;
    }
    example['Extra minutes'] = '25';
    const csv = Papa.unparse({ fields: headers, data: [example] });
    // The BOM is what makes Excel open this as UTF-8 rather than mangling every Chinese name.
    const blob = new Blob([new Uint8Array([0xEF, 0xBB, 0xBF]), csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'sen_register_template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleDeleteEntry = async (entry) => {
    const placedIn = rooms.filter(r => (r.students || []).some(s => s.email === entry.email));
    const warn = placedIn.length
      ? t('senInvigilation.removeEntryPlaced', { count: placedIn.length })
      : '';
    if (!window.confirm(t('senInvigilation.removeEntryConfirm', { name: entry.name || entry.email, warning: warn }))) return;
    try {
      await deleteDoc(doc(db, 'schools', activeSchoolId, 'sen_student', entry.email));
    } catch (err) {
      console.error('Error deleting SEN register entry', err);
      alert(t('senInvigilation.removeEntryFailed', { message: err.message }));
    }
  };

  // --- Settings save --------------------------------------------------------------------------
  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await setDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'sen_invigilation_settings'), {
        editPermissions,
        accommodations,
        checklistTemplate: cleanChecklist(checklistTemplate),
      }, { merge: true });
    } catch (err) {
      console.error('Error saving SEN invigilation settings', err);
      alert(t('senInvigilation.saveSettingsFailed', { message: err.message }));
    } finally {
      setSavingSettings(false);
    }
  };

  const toggleEditRole = (role) => {
    setEditPermissions(prev => ({
      roles: prev.roles.includes(role) ? prev.roles.filter(r => r !== role) : [...prev.roles, role],
    }));
  };

  // --- Render ---------------------------------------------------------------------------------
  const TABS = [
    { id: 'rooms', label: t('senInvigilation.tabs.rooms'), icon: DoorOpen },
    ...(canSeeRegister ? [{ id: 'register', label: t('senInvigilation.tabs.register'), icon: ClipboardList }] : []),
    ...(canManage ? [{ id: 'settings', label: t('senInvigilation.tabs.settings'), icon: SettingsIcon }] : []),
  ];

  const totalUnplaced = useMemo(
    () => relevantSessions.reduce((n, s) => n + unplacedStudents(s, register, roomsBySession[s.id] || []).length, 0),
    [relevantSessions, register, roomsBySession],
  );

  return (
    <div className="new-theme">
      <div className="theme-tabs-bar">
        <div className="theme-tabs-group">
          {TABS.map(tab => (
            <button
              key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`theme-tab ${activeTab === tab.id ? 'active' : ''}`}
              style={{ whiteSpace: 'nowrap' }}
            >
              <span className="theme-tab-icon"><tab.icon size={16} /></span>
              {tab.label}
            </button>
          ))}
        </div>

        <HeaderActionsPortal>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            {activeTab === 'rooms' && (
              <>
                <select value={schoolYear} onChange={e => setSchoolYear(e.target.value)} style={selectStyle}>
                  {academicYears.map(y => <option key={y.uid} value={y.yearId}>{y.yearName || y.yearId}</option>)}
                </select>
                <select value={assessment} onChange={e => setAssessment(e.target.value)} style={selectStyle}>
                  {assessments.map(a => <option key={a.code} value={a.code}>{a.name || a.code}</option>)}
                </select>
              </>
            )}
            {activeTab === 'register' && canSeeRegister && (
              <>
                <input
                  ref={fileInputRef} type="file" accept=".csv,text/csv" onChange={handleChooseFile}
                  style={{ display: 'none' }}
                />
                <button className="btn-outline" onClick={handleDownloadTemplate}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', width: 'auto', padding: '0 0.9rem', whiteSpace: 'nowrap' }}>
                  <Download size={16} /> {t('senInvigilation.template')}
                </button>
                <button className="btn-outline" onClick={() => fileInputRef.current?.click()}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', width: 'auto', padding: '0 0.9rem', whiteSpace: 'nowrap' }}>
                  <Upload size={16} /> {t('senInvigilation.importCsv')}
                </button>
                <button className="btn-primary" onClick={openNewEntry}
                  style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', width: 'auto', padding: '0 0.9rem', whiteSpace: 'nowrap' }}>
                  <Plus size={16} /> {t('senInvigilation.addStudent')}
                </button>
              </>
            )}
            {activeTab === 'settings' && (
              <button className="btn-primary" onClick={handleSaveSettings} disabled={savingSettings}
                style={{ width: 'auto', padding: '0 1.25rem' }}>
                {savingSettings ? t('common.saving') : t('senInvigilation.saveSettings')}
              </button>
            )}
          </div>
        </HeaderActionsPortal>
      </div>

      {/* ── Special Rooms ────────────────────────────────────────────────────────────────── */}
      {activeTab === 'rooms' && (
        <div style={{ ...card(isMobile), flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: isMobile ? '0.75rem' : '1rem 1.25rem', borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
            <Accessibility size={18} style={{ color: 'var(--color-primary)' }} />
            <span style={{ fontWeight: '700', fontSize: '0.95rem' }}>
              {relevantSessions.length} sitting{relevantSessions.length === 1 ? '' : 's'} need a special room
            </span>
            {totalUnplaced > 0 && (
              <span style={{ ...chip, backgroundColor: 'var(--color-warning-bg)', color: 'var(--color-warning-text)' }}>
                <AlertTriangle size={12} /> {totalUnplaced} student placement{totalUnplaced === 1 ? '' : 's'} outstanding
              </span>
            )}
            {!canSeeRegister && (
              <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
                Showing the rooms only. The register is restricted to this module&apos;s administrators.
              </span>
            )}
          </div>

          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: isMobile ? '0.75rem' : '1.25rem', display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            {loadingSessions && <span style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>{t('senInvigilation.loadingSittings')}</span>}

            {!loadingSessions && sessionsByDate.length === 0 && (
              <div style={{ padding: '2rem 1rem', textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                <b style={{ fontSize: '0.95rem' }}>{t('senInvigilation.noSittingYet')}</b>
                <span style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>
                  {canSeeRegister
                    ? t('senInvigilation.noSittingAdmin')
                    : t('senInvigilation.noSittingStaff')}
                </span>
              </div>
            )}

            {sessionsByDate.map(([date, daySessions]) => (
              <div key={date} style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
                  {date}
                </div>

                {daySessions.map(session => {
                  const sessionRooms = roomsBySession[session.id] || [];
                  const unplaced = unplacedStudents(session, register, sessionRooms);
                  const registered = senStudentsForSession(session, register);
                  return (
                    <div key={session.id} style={{ border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                      <div style={{ padding: '0.7rem 0.9rem', backgroundColor: 'var(--color-bg-subtle)', display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
                        <span style={{ fontWeight: '700', fontSize: '0.9rem' }}>
                          {[session.class, session.subject, session.paper].filter(Boolean).join(' ')}
                        </span>
                        <span style={{ ...chip }}>
                          <Clock size={12} />
                          {session.startTime || '--:--'}
                          {session.duration ? ` · ${session.duration} min` : ''}
                        </span>
                        {session.venue && <span style={chip}>{session.venue}</span>}

                        {canSeeRegister && registered.length > 0 && (
                          unplaced.length === 0
                            ? (
                              <span style={{ ...chip, backgroundColor: 'var(--color-success-bg)', color: 'var(--color-success-text)' }}>
                                <CheckCircle2 size={12} /> all {registered.length} placed
                              </span>
                            ) : (
                              <span style={{ ...chip, backgroundColor: 'var(--color-warning-bg)', color: 'var(--color-warning-text)' }}>
                                <AlertTriangle size={12} /> {unplaced.length} of {registered.length} unplaced
                              </span>
                            )
                        )}

                        {canManage && (
                          <button className="btn-outline" onClick={() => openNewRoom(session)}
                            style={{ marginLeft: 'auto', width: 'auto', padding: '0.3rem 0.7rem', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                            <Plus size={14} /> {t('senInvigilation.specialRoom')}
                          </button>
                        )}
                      </div>

                      {sessionRooms.length === 0 && (
                        <div style={{ padding: '0.75rem 0.9rem', fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>
                          {t('senInvigilation.noSpecialRoom')}
                          {canManage ? ' Add one to place these students.' : ''}
                        </div>
                      )}

                      {sessionRooms.map(room => (
                        <div key={room.id} style={{ padding: '0.75rem 0.9rem', borderTop: '1px solid var(--color-border)', display: 'flex', gap: '0.75rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                          <div style={{ flex: '1 1 220px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
                              <DoorOpen size={14} style={{ color: 'var(--color-primary)' }} />
                              <b style={{ fontSize: '0.875rem' }}>{room.venue || t('senInvigilation.venueNotSet')}</b>
                              <span style={{ ...chip, backgroundColor: 'var(--color-info-bg)', color: 'var(--color-info-text)' }}>
                                {room.startTime || '--:--'} to {room.endTime || '--:--'}
                              </span>
                              {room.extraMinutes > 0 && (
                                <span style={chip}>+{room.extraMinutes} {t('senInvigilation.min')}</span>
                              )}
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                              {(room.students || []).length === 0 && (
                                <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>{t('senInvigilation.nobodyInRoom')}</span>
                              )}
                              {(room.students || []).map(s => (
                                <span key={s.email} style={chip}>
                                  {[s.currentClass, s.classNumber].filter(Boolean).join('-')} {s.name}
                                </span>
                              ))}
                            </div>
                            {(room.checklist || []).length > 0 && (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.15rem', marginTop: '0.15rem' }}>
                                {resolveChecklist(room.checklist, { startTime: room.startTime, endTime: room.endTime }).map(it => (
                                  <div key={it.id} style={{ display: 'flex', gap: '0.45rem', alignItems: 'baseline', fontSize: '0.78rem' }}>
                                    <span style={{ fontWeight: '700', color: 'var(--color-primary)', fontVariantNumeric: 'tabular-nums', minWidth: '2.9rem' }}>
                                      {it.time || '--:--'}
                                    </span>
                                    <span style={{ color: 'var(--color-text-subtle)' }}>{it.text}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {/* Only ever shown for a room saved before the running order existed. */}
                            {!(room.checklist || []).length && room.notes && (
                              <span style={{ fontSize: '0.78rem', color: 'var(--color-text-subtle)' }}>{room.notes}</span>
                            )}
                          </div>

                          <div style={{ flex: '0 1 200px', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                            <span style={{ fontSize: '0.72rem', fontWeight: '700', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
                              {t('senInvigilation.invigilators')}
                            </span>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                              {(room.invigilators || []).length === 0 && (
                                <span style={{ ...chip, backgroundColor: 'var(--color-warning-bg)', color: 'var(--color-warning-text)' }}>none assigned</span>
                              )}
                              {(room.invigilators || []).map(ini => (
                                <span key={ini} style={{ ...chip, backgroundColor: 'var(--color-primary-light)', color: 'var(--color-primary)' }}>{ini}</span>
                              ))}
                            </div>
                          </div>

                          {canManage && (
                            <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center' }}>
                              <button type="button" onClick={() => openEditRoom(room)} style={{ ...rowBtn, color: 'var(--color-primary)' }} aria-label={t('senInvigilation.editRoom')}>
                                <Pencil size={15} />
                              </button>
                              <button type="button" onClick={() => handleDeleteRoom(room)} style={{ ...rowBtn, color: 'var(--color-danger)' }} aria-label={t('senInvigilation.deleteRoom')}>
                                <Trash2 size={15} />
                              </button>
                            </div>
                          )}
                        </div>
                      ))}

                      {canSeeRegister && unplaced.length > 0 && (
                        <div style={{ padding: '0.6rem 0.9rem', borderTop: '1px solid var(--color-border)', backgroundColor: 'var(--color-warning-bg)', display: 'flex', gap: '0.4rem', alignItems: 'center', flexWrap: 'wrap' }}>
                          <AlertTriangle size={13} style={{ color: 'var(--color-warning-text)' }} />
                          <span style={{ fontSize: '0.78rem', color: 'var(--color-warning-text)', fontWeight: '600' }}>{t('senInvigilation.notYetInARoom')}</span>
                          {unplaced.map(s => (
                            <span key={s.email} style={{ ...chip, backgroundColor: 'var(--color-bg-card)', color: 'var(--color-warning-text)' }}>
                              {[s.currentClass, s.classNumber].filter(Boolean).join('-')} {s.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── SEN Register ─────────────────────────────────────────────────────────────────── */}
      {activeTab === 'register' && canSeeRegister && (
        <div style={{ ...card(isMobile), flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: isMobile ? '0.75rem' : '1rem 1.25rem', borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flex: '1 1 220px', maxWidth: '340px' }}>
              <Search size={15} style={{ color: 'var(--color-text-muted)' }} />
              <input
                className="input-field" value={registerSearch} onChange={e => setRegisterSearch(e.target.value)}
                placeholder={t('senInvigilation.searchStudent')} style={{ padding: '0.4rem 0.6rem', fontSize: '0.85rem' }}
              />
            </div>
            <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
              {filteredRegister.length} of {register.length} on the register
            </span>
          </div>

          <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--color-bg-app)' }}>
                  <th style={th}>{t('senInvigilation.className')}</th>
                  <th style={th}>{t('senInvigilation.classNumber')}</th>
                  <th style={th}>{t('senInvigilation.name')}</th>
                  <th style={th}>{t('senInvigilation.accommodations')}</th>
                  <th style={th}>{t('senInvigilation.extraTime')}</th>
                  <th style={th}>{t('senInvigilation.notes')}</th>
                  {canManage && <th style={th} />}
                </tr>
              </thead>
              <tbody>
                {filteredRegister.length === 0 && (
                  <tr>
                    <td style={{ ...td, textAlign: 'center', color: 'var(--color-text-muted)', padding: '2rem 1rem' }} colSpan={canManage ? 7 : 6}>
                      {register.length === 0
                        ? t('senInvigilation.registerEmpty')
                        : t('senInvigilation.noStudentMatches')}
                    </td>
                  </tr>
                )}
                {filteredRegister.map(entry => (
                  <tr key={entry.email} style={{ opacity: entry.active === false ? 0.55 : 1 }}>
                    <td style={td}>{entry.currentClass}</td>
                    <td style={td}>{entry.classNumber}</td>
                    <td style={{ ...td, fontWeight: '600' }}>
                      {entry.name}
                      {entry.active === false && <span style={{ ...chip, marginLeft: '0.4rem' }}>{t('senInvigilation.inactive')}</span>}
                    </td>
                    <td style={td}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                        {(entry.accommodations || []).length === 0 && <span style={{ color: 'var(--color-text-muted)' }}>none recorded</span>}
                        {(entry.accommodations || []).map(code => (
                          <span key={code} style={chip}>{accommodationLabel[code] || code}</span>
                        ))}
                      </div>
                    </td>
                    <td style={td}>{entry.extraMinutes ? `+${entry.extraMinutes} min` : '-'}</td>
                    <td style={{ ...td, color: 'var(--color-text-subtle)', whiteSpace: 'pre-wrap' }}>{entry.notes || ''}</td>
                    {canManage && (
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', gap: '0.3rem' }}>
                          <button type="button" onClick={() => openEditEntry(entry)} style={{ ...rowBtn, color: 'var(--color-primary)' }} aria-label={t('senInvigilation.editEntry')}>
                            <Pencil size={15} />
                          </button>
                          <button type="button" onClick={() => handleDeleteEntry(entry)} style={{ ...rowBtn, color: 'var(--color-danger)' }} aria-label={t('senInvigilation.removeEntry')}>
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Settings ─────────────────────────────────────────────────────────────────────── */}
      {activeTab === 'settings' && canManage && (
        <div style={{ ...card(isMobile), flex: 1, minHeight: 0, overflowY: 'auto', padding: isMobile ? '0.75rem' : '1.25rem', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxWidth: '46rem' }}>
            <b style={{ fontSize: '0.9rem' }}>{t('senInvigilation.whoCanArrange')}</b>
            <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
              {t('senInvigilation.whoCanArrangeIntro')}
            </span>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
              {['Teacher', 'Staff'].map(role => (
                <label key={role} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.875rem' }}>
                  <input type="checkbox" checked={editPermissions.roles.includes(role)} onChange={() => toggleEditRole(role)} />
                  {role}
                </label>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxWidth: '52rem' }}>
            <b style={{ fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
              <ListChecks size={16} style={{ color: 'var(--color-primary)' }} /> {t('senInvigilation.defaultRunningOrder')}
            </b>
            <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
              {t('senInvigilation.defaultRunningOrderIntro')}
            </span>
            <div style={{ marginTop: '0.25rem' }}>
              <ChecklistEditor items={checklistTemplate} onChange={setChecklistTemplate} times={null} isMobile={isMobile} />
            </div>
            {checklistTemplate.length > 0 && (
              <div style={{ marginTop: '0.35rem', display: 'flex', flexDirection: 'column', gap: '0.1rem' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: '700', letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
                  {t('senInvigilation.readsAs')}
                </span>
                {checklistTemplate.filter(it => it.text).map(it => (
                  <span key={it.id} style={{ fontSize: '0.78rem', color: 'var(--color-text-subtle)' }}>
                    {describeItem(it)}: {it.text}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', maxWidth: '46rem' }}>
            <b style={{ fontSize: '0.9rem' }}>{t('senInvigilation.accommodationTypes')}</b>
            <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
              {t('senInvigilation.accommodationTypesIntro')}
            </span>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginTop: '0.25rem' }}>
              {accommodations.map((a, i) => (
                <div key={a.code} style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                  <input
                    className="input-field" value={a.label}
                    onChange={e => setAccommodations(prev => prev.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                    style={{ padding: '0.4rem 0.6rem', fontSize: '0.85rem', maxWidth: '20rem' }}
                  />
                  <code style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{a.code}</code>
                  <button
                    type="button" aria-label={t('senInvigilation.removeType')}
                    onClick={() => setAccommodations(prev => prev.filter((_, j) => j !== i))}
                    style={{ ...rowBtn, color: 'var(--color-danger)', marginLeft: 'auto' }}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              ))}
              <button
                type="button" className="btn-outline"
                onClick={() => setAccommodations(prev => [...prev, { code: `custom_${prev.length + 1}`, label: t('senInvigilation.newAccommodation') }])}
                style={{ width: 'auto', padding: '0.4rem 0.9rem', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}
              >
                <Plus size={14} /> {t('senInvigilation.addType')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Room editor ──────────────────────────────────────────────────────────────────── */}
      {roomForm && roomSession && (
        <div onClick={() => { if (!savingRoom) setRoomForm(null); }}
          style={{ position: 'fixed', inset: 0, zIndex: 120, backgroundColor: 'var(--color-overlay)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : '2rem' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: isMobile ? '100%' : '760px', maxHeight: isMobile ? '92vh' : '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: isMobile ? '0.9rem 1rem' : '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border)', flexShrink: 0 }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>
                  {roomForm.id ? t('senInvigilation.editRoomTitle') : t('senInvigilation.newRoom')}
                </h3>
                <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                  {[roomSession.class, roomSession.subject, roomSession.paper].filter(Boolean).join(' ')}
                  {' · '}{roomSession.date} {roomSession.startTime}
                </span>
              </div>
              <button type="button" onClick={() => setRoomForm(null)} aria-label={t('common.close')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex', padding: '0.25rem' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveRoom} style={{ padding: isMobile ? '1rem' : '1.5rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 200px' }}>
                  <label className="input-label">{t('senInvigilation.venue')}</label>
                  <select className="input-field" value={roomForm.venue} onChange={e => setRoomForm(p => ({ ...p, venue: e.target.value }))}>
                    <option value="">{t('senInvigilation.notSet')}</option>
                    {locations.map(l => <option key={l.code} value={l.code}>{l.name || l.code}</option>)}
                  </select>
                </div>
                <div style={{ flex: '0 1 150px' }}>
                  <label className="input-label">{t('senInvigilation.extraTimeMin')}</label>
                  <input
                    className="input-field" type="number" min="0" step="1" value={roomForm.extraMinutes}
                    onChange={e => setRoomForm(p => ({ ...p, extraMinutes: e.target.value }))}
                  />
                </div>
                <div style={{ flex: '1 1 180px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', paddingBottom: '0.2rem' }}>
                  <span style={{ fontSize: '0.8rem', color: 'var(--color-text-subtle)' }}>
                    {t('senInvigilation.roomTimes', { start: roomSession.startTime || '--:--', end: '' })}<b>{roomTimes.endTime || '--:--'}</b>
                    {roomTimes.extraMinutes > 0 ? t('senInvigilation.roomExtra', { count: roomTimes.extraMinutes }) : ''}
                  </span>
                  {suggestedMinutes > 0 && Number(roomForm.extraMinutes) !== suggestedMinutes && (
                    <button type="button" onClick={() => setRoomForm(p => ({ ...p, extraMinutes: suggestedMinutes }))}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--color-primary)', fontSize: '0.76rem', textAlign: 'left', textDecoration: 'underline' }}>
                      {t('senInvigilation.useSuggested', { count: suggestedMinutes })}
                    </button>
                  )}
                </div>
              </div>

              <div>
                <label className="input-label">
                  {t('senInvigilation.studentsPlaced', { count: roomForm.students.length })}
                </label>
                {/* Someone granted arranging through the role matrix, but not named as a module
                    admin, cannot read the register at all -- so they set up the room and its
                    invigilators, and whoever holds the register puts the students in it. Said
                    outright, because an empty picker with no explanation reads as a broken screen. */}
                {!canSeeRegister ? (
                  <span style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>
                    {roomForm.students.length > 0
                      ? t('senInvigilation.alreadyPlaced', { count: roomForm.students.length })
                      : t('senInvigilation.nobodyPlacedYet')}
                    {' '}{t('senInvigilation.cannotPlaceStudents')}
                  </span>
                ) : roomCandidates.length === 0 && (
                  <span style={{ fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>
                    {t('senInvigilation.nobodyLeftToPlace', { class: roomSession.class })}
                  </span>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.2rem', maxHeight: '13rem', overflowY: 'auto', border: (canSeeRegister && roomCandidates.length) ? '1px solid var(--color-border)' : 'none', borderRadius: 'var(--radius-md)', padding: (canSeeRegister && roomCandidates.length) ? '0.5rem' : 0 }}>
                  {canSeeRegister && roomCandidates.map(s => (
                    <label key={s.email} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', padding: '0.2rem 0.1rem' }}>
                      <input
                        type="checkbox"
                        checked={roomForm.students.some(x => x.email === s.email)}
                        onChange={() => toggleRoomStudent(s)}
                      />
                      <span style={{ minWidth: '3.5rem', color: 'var(--color-text-subtle)' }}>
                        {[s.currentClass, s.classNumber].filter(Boolean).join('-')}
                      </span>
                      <span style={{ fontWeight: '600' }}>{s.name}</span>
                      <span style={{ marginLeft: 'auto', display: 'flex', gap: '0.25rem', flexWrap: 'wrap' }}>
                        {s.extraMinutes ? <span style={chip}>+{s.extraMinutes} min</span> : null}
                        {(s.accommodations || []).map(c => (
                          <span key={c} style={chip}>{accommodationLabel[c] || c}</span>
                        ))}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="input-label">{t('senInvigilation.invigilators')}</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginBottom: '0.4rem' }}>
                  {roomForm.invigilators.length === 0 && (
                    <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>{t('senInvigilation.nobodyAssigned')}</span>
                  )}
                  {roomForm.invigilators.map(ini => (
                    <span key={ini} style={{ ...chip, backgroundColor: 'var(--color-primary-light)', color: 'var(--color-primary)' }}>
                      {ini}
                      <button type="button" onClick={() => setRoomForm(p => ({ ...p, invigilators: p.invigilators.filter(x => x !== ini) }))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-danger)', display: 'flex', padding: 0 }}>
                        <X size={10} />
                      </button>
                    </span>
                  ))}
                </div>
                <select
                  className="input-field" value=""
                  onChange={e => {
                    const ini = e.target.value;
                    if (!ini) return;
                    setRoomForm(p => (p.invigilators.includes(ini) ? p : { ...p, invigilators: [...p.invigilators, ini] }));
                  }}
                >
                  <option value="">{t('senInvigilation.addInvigilator')}</option>
                  {staffList.filter(s => !roomForm.invigilators.includes(s.initial)).map(s => (
                    <option key={s.email} value={s.initial}>{s.initial} - {s.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="input-label">{t('senInvigilation.runningOrder')}</label>
                <span style={{ display: 'block', fontSize: '0.78rem', color: 'var(--color-text-muted)', marginBottom: '0.45rem' }}>
                  Times are worked out from this room, so a step set against the end moves by itself
                  when the extra time changes. Use a fixed time only for something really tied to the clock.
                </span>
                <ChecklistEditor
                  items={roomForm.checklist} onChange={next => setRoomForm(p => ({ ...p, checklist: next }))}
                  times={{ startTime: roomSession.startTime, endTime: roomTimes.endTime }} isMobile={isMobile}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', paddingTop: '0.25rem' }}>
                <button type="button" className="btn-outline" onClick={() => setRoomForm(null)} disabled={savingRoom} style={modalBtn}>{t('common.cancel')}</button>
                <button type="submit" className="btn-primary" disabled={savingRoom} style={modalBtn}>
                  {savingRoom ? t('common.saving') : t('senInvigilation.saveRoom')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Register entry editor ────────────────────────────────────────────────────────── */}
      {entryForm && (
        <div onClick={() => { if (!savingEntry) setEntryForm(null); }}
          style={{ position: 'fixed', inset: 0, zIndex: 120, backgroundColor: 'var(--color-overlay)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : '2rem' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: isMobile ? '100%' : '640px', maxHeight: isMobile ? '92vh' : '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: isMobile ? '0.9rem 1rem' : '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border)', flexShrink: 0 }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>
                {entryForm.email ? entryForm.name || t('senInvigilation.editEntry') : t('senInvigilation.addToRegister')}
              </h3>
              <button type="button" onClick={() => setEntryForm(null)} aria-label={t('common.close')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex', padding: '0.25rem' }}>
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSaveEntry} style={{ padding: isMobile ? '1rem' : '1.5rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {!entryForm.email && (
                <div>
                  <label className="input-label">{t('senInvigilation.student')}</label>
                  <input
                    className="input-field" value={studentSearch} onChange={e => setStudentSearch(e.target.value)}
                    placeholder={t('senInvigilation.searchStudent')} style={{ marginBottom: '0.4rem' }}
                  />
                  <div style={{ maxHeight: '13rem', overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
                    {studentPickList.length === 0 && (
                      <div style={{ padding: '0.75rem', fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>
                        {t('senInvigilation.noStudentToAdd')}
                      </div>
                    )}
                    {studentPickList.map(s => (
                      <button
                        key={s.email} type="button"
                        onClick={() => setEntryForm(p => ({
                          ...p, email: s.email, sid: s.sid, name: s.name,
                          currentClass: s.currentClass, classNumber: s.classNumber,
                        }))}
                        style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', borderBottom: '1px solid var(--color-border)', padding: '0.45rem 0.6rem', cursor: 'pointer', fontSize: '0.85rem', display: 'flex', gap: '0.5rem' }}
                      >
                        <span style={{ minWidth: '3.5rem', color: 'var(--color-text-subtle)' }}>
                          {[s.currentClass, s.classNumber].filter(Boolean).join('-')}
                        </span>
                        <span style={{ fontWeight: '600' }}>{s.name}</span>
                        <span style={{ marginLeft: 'auto', color: 'var(--color-text-muted)' }}>{s.sid}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {entryForm.email && (
                <>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap', fontSize: '0.85rem' }}>
                    <span style={chip}>{[entryForm.currentClass, entryForm.classNumber].filter(Boolean).join('-')}</span>
                    <b>{entryForm.name}</b>
                    <span style={{ color: 'var(--color-text-muted)' }}>{entryForm.sid}</span>
                  </div>

                  <div>
                    <label className="input-label">{t('senInvigilation.accommodations')}</label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1rem' }}>
                      {accommodations.map(a => (
                        <label key={a.code} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
                          <input
                            type="checkbox" checked={entryForm.accommodations.includes(a.code)}
                            onChange={() => toggleEntryAccommodation(a.code)}
                          />
                          {a.label}
                        </label>
                      ))}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                    <div style={{ flex: '0 1 150px' }}>
                      <label className="input-label">{t('senInvigilation.extraTimeMin')}</label>
                      <input
                        className="input-field" type="number" min="0" step="1" value={entryForm.extraMinutes}
                        onChange={e => setEntryForm(p => ({ ...p, extraMinutes: e.target.value }))}
                      />
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', paddingBottom: '0.7rem' }}>
                      <input
                        type="checkbox" checked={entryForm.active !== false}
                        onChange={e => setEntryForm(p => ({ ...p, active: e.target.checked }))}
                      />
                      On the register this year
                    </label>
                  </div>

                  <div>
                    <label className="input-label">{t('senInvigilation.notes')}</label>
                    <textarea
                      className="input-field" rows={3} value={entryForm.notes}
                      onChange={e => setEntryForm(p => ({ ...p, notes: e.target.value }))}
                      style={{ margin: 0, resize: 'vertical', fontFamily: 'inherit' }}
                      placeholder={t('senInvigilation.notesPlaceholder')}
                    />
                  </div>
                </>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', paddingTop: '0.25rem' }}>
                <button type="button" className="btn-outline" onClick={() => setEntryForm(null)} disabled={savingEntry} style={modalBtn}>{t('common.cancel')}</button>
                <button type="submit" className="btn-primary" disabled={savingEntry || !entryForm.email} style={modalBtn}>
                  {savingEntry ? t('common.saving') : t('senInvigilation.saveEntry')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* --- Import preview ------------------------------------------------------------------ */}
      {importPreview && (
        <div onClick={() => { if (!importing) setImportPreview(null); }}
          style={{ position: 'fixed', inset: 0, zIndex: 130, backgroundColor: 'var(--color-overlay)', display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center', padding: isMobile ? 0 : '2rem' }}>
          <div onClick={e => e.stopPropagation()}
            style={{ backgroundColor: 'var(--color-bg-card)', borderRadius: isMobile ? 'var(--radius-lg) var(--radius-lg) 0 0' : 'var(--radius-lg)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', boxShadow: 'var(--shadow-lg)', width: '100%', maxWidth: isMobile ? '100%' : '700px', maxHeight: isMobile ? '92vh' : '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: isMobile ? '0.9rem 1rem' : '1.25rem 1.5rem', borderBottom: '1px solid var(--color-border)', flexShrink: 0 }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>{t('senInvigilation.beforeImporting')}</h3>
                <span style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>{importPreview.fileName}</span>
              </div>
              <button type="button" onClick={() => setImportPreview(null)} aria-label={t('common.close')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', display: 'flex', padding: '0.25rem' }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: isMobile ? '1rem' : '1.5rem', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <b style={{ fontSize: '1.5rem', lineHeight: 1, color: 'var(--color-primary)' }}>{importPreview.entries.length}</b>
                  <span style={{ fontSize: '0.76rem', color: 'var(--color-text-muted)' }}>{t('senInvigilation.rowsMatched')}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <b style={{ fontSize: '1.5rem', lineHeight: 1 }}>{importPreview.updating}</b>
                  <span style={{ fontSize: '0.76rem', color: 'var(--color-text-muted)' }}>{t('senInvigilation.willBeUpdated')}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <b style={{ fontSize: '1.5rem', lineHeight: 1, color: importPreview.problems.some(pr => !pr.warning) ? 'var(--color-danger)' : 'var(--color-text-main)' }}>
                    {importPreview.problems.filter(pr => !pr.warning).length}
                  </b>
                  <span style={{ fontSize: '0.76rem', color: 'var(--color-text-muted)' }}>{t('senInvigilation.rowsSkipped')}</span>
                </div>
              </div>

              {/* Merged, not replaced. Saying so is the difference between "the file is now the
                  register" and "the file was added to the register", which are not the same thing. */}
              {importPreview.untouched.length > 0 && (
                <div style={{ padding: '0.7rem 0.9rem', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--color-info-bg)', color: 'var(--color-info-text)', fontSize: '0.82rem' }}>
                  {t('senInvigilation.untouched', { count: importPreview.untouched.length })}
                </div>
              )}

              {importPreview.entries.length === 0 && (
                <div style={{ padding: '0.7rem 0.9rem', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--color-danger-bg)', color: 'var(--color-danger-text)', fontSize: '0.82rem' }}>
                  {t('senInvigilation.nothingMatched')}
                </div>
              )}

              {importPreview.problems.length > 0 && (
                <div>
                  <b style={{ fontSize: '0.85rem' }}>{t('senInvigilation.problems', { count: importPreview.problems.length })}</b>
                  <div style={{ marginTop: '0.4rem', maxHeight: '14rem', overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
                    {importPreview.problems.map((pr, i) => (
                      <div key={`${pr.line}-${i}`}
                        style={{ padding: '0.4rem 0.6rem', borderBottom: '1px solid var(--color-border)', fontSize: '0.8rem', display: 'flex', gap: '0.5rem', alignItems: 'baseline' }}>
                        <span style={{ ...chip, backgroundColor: pr.warning ? 'var(--color-warning-bg)' : 'var(--color-danger-bg)', color: pr.warning ? 'var(--color-warning-text)' : 'var(--color-danger-text)' }}>
                          {pr.warning ? t('senInvigilation.kept') : t('senInvigilation.skipped')}
                        </span>
                        <span style={{ color: 'var(--color-text-muted)', minWidth: '3.5rem' }}>{t('senInvigilation.row', { line: pr.line })}</span>
                        <span>{pr.who ? `${pr.who}: ` : ''}{pr.reason}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {importPreview.entries.length > 0 && (
                <div>
                  <b style={{ fontSize: '0.85rem' }}>{t('senInvigilation.whatWillBeWritten')}</b>
                  <div style={{ marginTop: '0.4rem', maxHeight: '16rem', overflow: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: 'var(--color-bg-app)' }}>
                          <th style={th}>{t('senInvigilation.className')}</th>
                          <th style={th}>{t('senInvigilation.name')}</th>
                          <th style={th}>{t('senInvigilation.accommodations')}</th>
                          <th style={th}>{t('senInvigilation.extraTime')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {importPreview.entries.map(en => (
                          <tr key={en.email}>
                            <td style={td}>{[en.currentClass, en.classNumber].filter(Boolean).join('-')}</td>
                            <td style={{ ...td, fontWeight: '600' }}>{en.name}</td>
                            <td style={td}>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem' }}>
                                {en.accommodations.length === 0 && <span style={{ color: 'var(--color-text-muted)' }}>{t('senInvigilation.none')}</span>}
                                {en.accommodations.map(c => <span key={c} style={chip}>{accommodationLabel[c] || c}</span>)}
                              </div>
                            </td>
                            <td style={td}>{en.extraMinutes ? `+${en.extraMinutes} min` : '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                <button type="button" className="btn-outline" onClick={() => setImportPreview(null)} disabled={importing} style={modalBtn}>{t('common.cancel')}</button>
                <button
                  type="button" className="btn-primary" onClick={handleConfirmImport}
                  disabled={importing || importPreview.entries.length === 0} style={modalBtn}
                >
                  {importing ? t('senInvigilation.importing') : t('senInvigilation.importCount', { count: importPreview.entries.length })}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SENInvigilation;
